// React
import { Fragment, useContext, useEffect, useState } from "react"

// Components
import {
  BaseTable,
  CancelButton,
  CommandButton,
  Loading,
  PrintTransaction
} from "components"
import { ActionButton } from "../components"
import { AttachmentModal, Body, DeleteModal, InputSection } from "./components"

// Contexts
import { AccountProvider } from "contexts"
import { PoContext } from "./contexts"

// Form
import { yupResolver } from "@hookform/resolvers/yup"
import { FormProvider, useForm, useFormContext } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Utils
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import {
  useApi,
  useCountryById,
  useHandleAuth,
  useProvinceById,
  useRegencyById,
  useSubdistrictById,
  useToggle
} from "utils"
import {
  useEmployeeDetail,
  useSubdomainSetting,
  useVendorDetail
} from "../PurchaseOrder/utils"
import {
  fetchCurrentBalance,
  type FormType,
  useDepositDetail,
  validationSchema
} from "./utils"
import { useApprovalStore } from "../utils"

export default function EditDepositVendor(): JSX.Element {
  // Hooks
  const { deposit_vendor_id } = useParams()
  const { data, isLoading } = useDepositDetail(deposit_vendor_id!)

  const { setApproval, resetApproval } = useApprovalStore()

  useEffect(() => {
    resetApproval()
    setApproval(data?.approve_status || 0)
    return () => resetApproval()

  }, [data, setApproval, resetApproval])

  if (isLoading || !data) {
    return <Loading loading={isLoading} />
  }

  return (
    <PoContext.Provider value={data}>
      <AccountProvider>
        <FormSection />
      </AccountProvider>
    </PoContext.Provider>
  )
}

function FormSection(): JSX.Element {
  // Hooks
  const api = useApi()
  const data = useContext(PoContext)
  const navigate = useNavigate()
  const { deposit_vendor_id } = useParams()

  // Vars
  const datapo = data.datapo

  // Form
  const defaultValues: FormType = {
    _balance: 0,
    amount: data.amount,
    coa_id: data.coa_id,
    memo: data.memo,
    no_cheque: data.no_cheque,
    trx_code: data.trx_code,
    transaction_date: data.transaction_date,
    vendor_id: datapo.vendor_id,
    imgPath: data.attachment
  }
  const onSubmit = (value: FormType) => {
    const finalValue = {
      deposit_vendor_id,
      purchase_order_id: data.purchase_order_id,
      coa_id: value.coa_id,
      vendor_id: value.vendor_id,
      location_id: datapo.location_id,
      project_id: null,
      trx_code: value.trx_code,
      transaction_date: value.transaction_date,
      amount: value.amount,
      memo: value.memo,
      no_cheque: value.no_cheque,
      imgPath: value.imgPath || []
    }

    return new Promise<void>((resolve) => {
      fetchCurrentBalance(api, data.purchase_order_id.toString()).then(
        (res) => {
          if (res.totaldeposit + finalValue.amount > res.totalpo) {
            toast.error(
              "All deposit amount must be lower or equal to total amount of the purchase order"
            )
            return resolve()
          }

          toast
            .promise(api.put("/depositvendor/editdeposit", finalValue), {
              loading: "Loading...",
              success: (res) => res.data.message,
              error: (err) =>
                err.response.data.detail?.message ?? err.response.data.message
            })
            .then(() => {
              navigate(-1)
            })
            .catch(() => {})
            .finally(resolve)
        }
      )
    })
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })

  return (
    <FormProvider {...methods}>
      <InputSection
        actionsection={
          <ActionButton>
            <section className="flex flex-wrap gap-3">
              {/* {data.approve_status === 4 ? ( */}
                <Print /> 
              {/* ) : (
                <CommandButton actiontype="print" />
              )} */}

              <AttachmentModal
                code={methods?.control?._formValues?.trx_code!}
              />
            </section>

            <section className="flex flex-wrap gap-3">
              <CancelButton />

              {data.approve_status === 1 && <Delete />}

              {data.approve_status !== 4 && (
                <CommandButton
                  actiontype="save"
                  loading={methods.formState.isSubmitting ? "true" : undefined}
                  onClick={methods.handleSubmit(onSubmit)}
                />
              )}
            </section>
          </ActionButton>
        }
      />
    </FormProvider>
  )
}

function Delete(): JSX.Element {
  // Hooks
  const navigate = useNavigate()
  const { deposit_vendor_id } = useParams()
  const { isActive, toggle } = useToggle(false)

  return (
    <Fragment>
      <CommandButton actiontype="delete" onClick={toggle} />

      {isActive && (
        <DeleteModal
          deposit_vendor_id={deposit_vendor_id!}
          toggle={toggle}
          onSuccess={() => navigate(-1)}
        />
      )}
    </Fragment>
  )
}

export function Print({ trxCode }: { trxCode?: string }): JSX.Element {
  const { formState: { defaultValues } } = useFormContext<FormType>()

  const { approve_by } = useContext(PoContext)
  const approvedBy = useEmployeeDetail(approve_by)
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const setting = useSubdomainSetting()
  const subdistrict = useSubdistrictById()
  const vendor = useVendorDetail(defaultValues?.vendor_id)
  const { userTokenData } = useHandleAuth()
  const [address, setAddress] = useState<string>("")

  const vendorData = vendor.data
  const userToken = userTokenData()

  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }
  const getVendorAddress = (): string => {
    let generateAddress = vendorData?.address ?? ""

    if (vendorData?.subdistrict_name) {
      generateAddress += ", " + vendorData.subdistrict_name
    }

    if (vendorData?.regency_name) {
      generateAddress += ", " + vendorData.regency_name
    }

    if (vendorData?.province_name) {
      generateAddress += ", " + vendorData.province_name
    }

    if (vendorData?.country_name) {
      generateAddress += ", " + vendorData.country_name
    }

    return generateAddress
  }

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  return (
    <PrintTransaction
      loading={
        company.isLoading ||
        country.isLoading ||
        province.isLoading ||
        subdistrict.isLoading ||
        regency.isLoading
      }>
      <section className="flex flex-col gap-3">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <tr>
                  <td className="pr-2">ADDRESS</td>
                  <td>: {getAddress()}</td>
                </tr>

                <tr>
                  <td>PHONE</td>
                  <td> : {setting.data?.phone ? setting.data?.phone : "-"}</td>
                </tr>

                <tr>
                  <td>EMAIL</td>
                  <td> : {setting.data?.email ? setting.data?.email : "-"}</td>
                </tr>

                <tr>
                  <td>WEBSITE</td>
                  <td>
                    : {setting.data?.website ? setting.data?.website : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">DEPOSIT VENDOR</div>

            <table>
              <tbody className="font-bold ">
                <tr>
                  <td width={10} className="text-right">
                    DATE :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {defaultValues?.transaction_date
                      ? moment(defaultValues?.transaction_date).format(
                          "DD/MM/YYYY"
                        )
                      : "-"}
                  </td>
                </tr>

                <tr>
                  <td width={10} className="whitespace-nowrap">
                    DEPOSIT VENDOR NO :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {trxCode ? trxCode : defaultValues?.trx_code}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <tbody>
            <tr className="bg-black text-white font-bold">
              <td colSpan={2}>VENDOR INFORMATION</td>
            </tr>

            <tr>
              <td colSpan={2}>
                <div>VENDOR NAME</div>
                <div className="font-bold">{vendorData?.vendor_name}</div>
              </td>
            </tr>

            <tr>
              <td colSpan={2}>
                <div>ADDRESS</div>
                <div className="font-bold">{getVendorAddress()}</div>
              </td>
            </tr>

            <tr>
              <td>
                <div>CONTACT NO</div>
                <div className="font-bold">{vendorData?.handphone}</div>
              </td>

              <td>
                <div>EMAIL ADDRESS</div>
                <div className="font-bold">{vendorData?.email}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <BaseTable>
          <thead>
            <tr className="bg-black text-white font-bold">
              <td colSpan={5}>JOURNAL ENTRY</td>
            </tr>
            <tr>
              <th>ACCOUNT</th>
              <th>DEBET</th>
              <th>CREDIT</th>
            </tr>
          </thead>

          <tbody>
            <Body
              showDateColumn={false}
              showIdColumn={false}
              showMemoColumn={false}
            />
          </tbody>
        </BaseTable>

        <section className="grid grid-cols-5 items-stretch gap-5">
          <section className="col-span-2">
            <table className="table table-bordered">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Additional Notes:</td>
                </tr>

                <tr className="min-h-[300px]">
                  <td>{defaultValues?.memo}</td>
                </tr>
              </tbody>
            </table>
          </section>
          <div />

          <section className="col-span-2">
            <table className="w-full">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Authorized By</td>
                </tr>

                <tr className="h-[100px]" />

                <tr>
                  <td>{approvedBy.data?.employee_name}</td>
                </tr>

                <tr>
                  <td>{approvedBy.data?.job_position_name}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>
      </section>
    </PrintTransaction>
  )
}
